import { apiClient } from './client';
import { User } from '@/store/authStore';

/**
 * Admin 로그인 요청 데이터
 */
export interface AdminLoginRequest {
  adminId: string;
  adminPassword: string;
}

/**
 * Admin 로그인 응답 데이터
 */
export interface AdminLoginResponse {
  adminSeq: number;
  adminId: string;
  adminName: string;
  adminType: 'SUPER_ADMIN' | 'ADMIN';
  accessToken: string;
  refreshToken: string;
}

/**
 * 로그인 요청 데이터 (기존 호환성)
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * 로그인 응답 데이터 (기존 호환성)
 */
export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
}

/**
 * 인증 관련 API 서비스
 */
export const authApi = {
  /**
   * Admin 로그인
   */
  adminLogin: async (data: AdminLoginRequest): Promise<AdminLoginResponse> => {
    const response = await apiClient.post<AdminLoginResponse>('/admin/auth/login', data);
    
    // 토큰을 localStorage에 저장
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth-token', response.data.accessToken);
      localStorage.setItem('refresh-token', response.data.refreshToken);
    }
    
    return response.data;
  },

  /**
   * 로그인 (기존 호환성)
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', data);
    
    // 토큰을 localStorage에 저장
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth-token', response.data.token);
      localStorage.setItem('refresh-token', response.data.refreshToken);
    }
    
    return response.data;
  },

  /**
   * 로그아웃
   */
  logout: async (): Promise<void> => {
    try {
      const refreshToken = typeof window !== 'undefined' 
        ? localStorage.getItem('refresh-token') 
        : null;
      
      if (refreshToken) {
        await apiClient.post('/admin/auth/logout', { refreshToken });
      }
    } finally {
      // 토큰 제거
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth-token');
        localStorage.removeItem('refresh-token');
      }
    }
  },

  /**
   * 토큰 갱신
   */
  refreshToken: async (): Promise<{ accessToken: string; refreshToken: string }> => {
    const refreshToken = typeof window !== 'undefined' 
      ? localStorage.getItem('refresh-token') 
      : null;
    
    if (!refreshToken) {
      throw new Error('Refresh token not found');
    }

    const response = await apiClient.post<{ accessToken: string; refreshToken: string }>('/admin/auth/refresh', {
      refreshToken,
    });

    // 새 토큰 저장
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth-token', response.data.accessToken);
      localStorage.setItem('refresh-token', response.data.refreshToken);
    }

    return response.data;
  },

  /**
   * 현재 사용자 정보 조회
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  /**
   * 비밀번호 변경
   */
  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> => {
    await apiClient.post('/auth/change-password', data);
  },

  /**
   * 프로필 업데이트
   */
  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await apiClient.patch<User>('/auth/profile', data);
    return response.data;
  },
};
